import { useEffect, useState } from 'react';
import { BrandMark } from '@/components/ui/brand-loader';

interface PageLoaderProps {
  /** Label shown beneath the animation. Defaults to "Loading" */
  label?: string;
}

/**
 * Full-screen page loader used as the Suspense fallback for all lazy-loaded routes.
 * Fades in smoothly on mount to avoid a jarring flash.
 */
export function PageLoader({ label }: PageLoaderProps) {
  const [visible, setVisible] = useState(false);

  // Slight delay before showing to avoid a flash on fast connections
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center
        bg-white dark:bg-gray-900
        transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading page'}
    >
      {/* Each sheet of the logo is drawn in and erased in turn */}
      <div className="mb-6 flex flex-col items-center gap-4">
        <BrandMark size={72} variant="loading" />
      </div>

      {/* Optional label */}
      <p
        className={`text-sm font-medium text-gray-400 dark:text-gray-500 tracking-widest uppercase
          transition-opacity duration-500 delay-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
      >
        {label ?? 'Loading'}
      </p>
    </div>
  );
}
